import React, { useCallback } from "react";
import { commitMutation, graphql } from "react-relay";
import { useHistory } from "react-router";

import cx from "classnames";

import ThemeContext from "~/common/ThemeContext";
import ViewportContext from "~/common/ViewportContext";
import { environment } from "~/models";
import { ReactComponent as EmptyStateIlloDark } from "~/notes/css/merge-stream-empty-state-illo-dark.svg";
import { ReactComponent as EmptyStateIllo } from "~/notes/css/merge-stream-empty-state-illo.svg";
import { Preview, SearchBox, StreamSelections } from "~/notes/MergeStream";
import CreateStreamModal, { type CreateStreamModalViewer } from "~/stream/CreateStreamModal";
import { Button, Tooltip } from "~/ui";
import { Modal } from "~/ui/legacyFellowIndex";
import { __globalModalContext, useModals } from "~/ui/modals";
import toast from "~/ui/toast";

import { openSummaryModal } from "./SummaryModal";

import type { AddStreamTabModalMutation } from "~/__generated__/AddStreamTabModalMutation.graphql";

import mergeStyles from "~/notes/css/MergeStream.module.css";

export const useAddStreamTabModal = (streamId: string, streamName: string, viewer: CreateStreamModalViewer) => {
	const modals = useModals();
	return useCallback(
		() =>
			modals.open(({ close }) => (
				<AddStreamTabModal
					onClose={close}
					stream={{ id: streamId, displayName: streamName }}
					createStreamModalViewer={viewer}
				/>
			)),
		[modals, streamId, streamName, viewer],
	);
};

type Props = {
	onClose: (added: boolean) => void;
	stream: {
		id: string;
		displayName: string;
	};
	createStreamModalViewer: CreateStreamModalViewer;
};

const addTabMutation = graphql`
	mutation AddStreamTabModalMutation($streamId: ID!, $otherStreamId: ID!, $share: Boolean) {
		StreamTab_AddFellowNotesTab(streamId: $streamId, otherStreamId: $otherStreamId, share: $share) {
			stream {
				id
				tabs {
					...TabsAndBookmarks_noteStreamTab @relay(mask: false)
				}
			}
		}
	}
`;

/**
 * Adds another stream as a tab to a given stream
 * @param streamId The stream to add the tab to
 * @param otherStreamId The stream to add as a tab
 * @param share Whether to share the tab with other users
 * @param onCompleted Callback to run after the mutation completes
 */
export const addStreamAsTab = (streamId: string, otherStreamId: string, share: boolean, onCompleted?: () => void) => {
	commitMutation<AddStreamTabModalMutation>(environment, {
		mutation: addTabMutation,
		variables: {
			streamId: streamId,
			otherStreamId,
			share,
		},
		onCompleted: (response, errors) => {
			if (errors) {
				toast.error("We had a problem adding a tab, try again");
				console.log("Error adding tab", JSON.stringify({ streamId, otherStreamId, share, errors }, null, 2));

				throw new Error((errors || []).map(e => e.message).join(";"));
			}
			toast.success("Tab successfully added");
			onCompleted?.();
		},
	});
};

export default function AddStreamTabModal({ onClose, stream, createStreamModalViewer }: Props) {
	const [searchQuery, setSearchQuery] = React.useState("");
	const [selectedStreamId, setSelectedStreamId] = React.useState<string | null>(null);
	const [addMutationLoading, setAddMutationLoading] = React.useState(false);
	const { isMobile } = React.useContext(ViewportContext);
	const modals = useModals();
	const history = useHistory();

	const doAddTab = React.useCallback(
		(otherStreamId, share, navigateToTab) => {
			setAddMutationLoading(true);

			const onCompleted = () => {
				setAddMutationLoading(false);
				onClose(true);
				if (navigateToTab) history.push(`/stream/${stream.id}/otherStream/${otherStreamId}/`);
			};

			addStreamAsTab(stream.id, otherStreamId, share, onCompleted);
		},
		[onClose, stream.id, history],
	);

	const onAddClicked = React.useCallback(async () => {
		if (!selectedStreamId || addMutationLoading) {
			return;
		}

		const res = await openSummaryModal(stream.id, selectedStreamId);

		if (!res) return;

		if (res.action === "add") {
			doAddTab(selectedStreamId, res.shareWithUsers, false);
		}
	}, [selectedStreamId, addMutationLoading, stream.id, doAddTab]);

	const onSearchQueryChange = React.useCallback((value: string) => {
		setSearchQuery(value);
		setSelectedStreamId(null);
	}, []);

	const onSelectedStreamChange = React.useCallback((streamId: string | null) => {
		setSelectedStreamId(streamId);
	}, []);

	const onCancel = React.useCallback(() => {
		onClose(false);
	}, [onClose]);

	const onStreamCreated = React.useCallback(
		(streamId: string) => {
			doAddTab(streamId, false, true);
		},
		[doAddTab],
	);

	const onCreateStreamClicked = React.useCallback(() => {
		modals.open(({ close }) => (
			<CreateStreamModal hideDescription viewer={createStreamModalViewer} onClose={close} onCreated={onStreamCreated} />
		));
	}, [createStreamModalViewer, modals, onStreamCreated]);

	const nextButtonDisabled = selectedStreamId === null || addMutationLoading;
	const nextButton = (
		<Button
			size="normal"
			variant="primary"
			disabled={nextButtonDisabled}
			className={mergeStyles.nextButton}
			onClick={onAddClicked}
		>
			Next
		</Button>
	);

	return (
		<Modal onClose={onCancel} width="62.5rem" className={mergeStyles.modal}>
			<Modal.Header onClose={onCancel}>
				<Modal.Title>Add Fellow notes as a tab</Modal.Title>
			</Modal.Header>
			<Modal.Body dark noPadding className={mergeStyles.modalBody}>
				<div className={mergeStyles.container}>
					<div className={mergeStyles.left}>
						<SearchBox query={searchQuery} onQueryChange={onSearchQueryChange} />
						<div className={mergeStyles.streamSelections} role="listbox">
							<StreamSelections
								includeOneOnOnes
								includeExternal
								ignorePartialAccess
								streamId={stream.id}
								searchQuery={searchQuery}
								selectedStreamId={selectedStreamId}
								onChange={onSelectedStreamChange}
							/>
						</div>
					</div>
					{isMobile ? null : selectedStreamId ? (
						<Preview sourceStreamId={selectedStreamId} destinationStreamId={stream.id} />
					) : (
						<PreviewPlaceholder displayName={stream.displayName} />
					)}
				</div>
			</Modal.Body>
			<Modal.Footer>
				<Modal.FooterSecondaryActions>
					<Button size="normal" variant="secondary" onClick={onCreateStreamClicked}>
						New series
					</Button>
				</Modal.FooterSecondaryActions>

				<Button size="normal" variant="secondary" onClick={onCancel}>
					Cancel
				</Button>
				<Tooltip disabled={!nextButtonDisabled} content="Select a series to bookmark">
					{/* Tippy does not show up on disabled elements for reasons, so we
							need to wrap the button in something that's not disabled for the
							tooltip to show up. */}
					{nextButtonDisabled ? <div>{nextButton}</div> : nextButton}
				</Tooltip>
			</Modal.Footer>
		</Modal>
	);
}

export function PreviewPlaceholder({ displayName }: { displayName: string }) {
	const { isMobile } = React.useContext(ViewportContext);
	const { isDarkMode } = React.useContext(ThemeContext);

	if (isMobile) {
		return null;
	}
	return (
		<div className={cx(mergeStyles.right, mergeStyles.content, mergeStyles.emptyStateContainer)}>
			<div className={mergeStyles.previewPlaceholder}>
				{isDarkMode ? <EmptyStateIlloDark /> : <EmptyStateIllo />}
				<p>
					Select a series whose notes will be bookmarked as a tab in <strong>{displayName}</strong>
				</p>
			</div>
		</div>
	);
}
